export interface FrameAnalysisRequest {
  frameNumber: number;
  timestamp: number;
  speakerLabel: string;
  occurrenceNumber: number;
}

export interface FrameAnalysisResult {
  speakerLabel: string;
  frameNumber: number;
  timestamp: number;
  frameUrl: string;
  hasLowerThird: boolean;
  name?: string;
  title?: string;
  company?: string;
  confidence: 'high' | 'medium' | 'low';
}

export interface TranscriptSegment {
  speaker: string;
  start: number;
  end: number;
  text: string;
}

/** A stretch where one speaker holds the floor, in seconds. */
export interface SpeakerTurnRange {
  speaker: string;
  start: number;
  end: number;
}

/**
 * How far past the start of a turn a representative frame is taken, so the cut
 * has landed and the speaker is settled in shot.
 */
const THUMBNAIL_SETTLE_SECONDS = 3;

/**
 * How much of a speaker's turn their row preview plays before looping.
 *
 * Long enough to see and hear who it is, short enough that it never wanders
 * into the next speaker. Lives here rather than with the player because it is
 * what makes a turn worth showing: `getRepresentativeTurnBySpeaker` picks the
 * first turn that can hold this much of one speaker.
 */
export const PREVIEW_WINDOW_SECONDS = 6;

/**
 * The shortest turn worth offering a play control for.
 *
 * `timeupdate` lands about four times a second, which is what drives the loop
 * back to the start of the window. A turn shorter than that cadence cannot be
 * played at all — the first event already sits past the window end, so every
 * event seeks and playback never advances. Anything under a second is a stutter
 * rather than a preview, so those rows keep their still and no play control.
 */
const MIN_PLAYABLE_TURN_SECONDS = 1;

/** Whether a turn is long enough to preview, rather than only to show. */
export function isTurnPlayable(
  turn: SpeakerTurnRange | undefined,
): turn is SpeakerTurnRange {
  return !!turn && turn.end - turn.start >= MIN_PLAYABLE_TURN_SECONDS;
}

/**
 * Get frame numbers for speaker analysis
 * Extracts frames 3 seconds after each speaker starts talking (up to 3 times per speaker)
 */
export function getFrameNumbersForSpeakerAnalysis(
  segments: TranscriptSegment[],
): FrameAnalysisRequest[] {
  const speakerFrames: FrameAnalysisRequest[] = [];
  const speakerOccurrences: Record<string, number> = {};

  segments.forEach((segment, index) => {
    const speaker = segment.speaker;

    // Initialize counter for new speakers
    if (!speakerOccurrences[speaker]) {
      speakerOccurrences[speaker] = 0;
    }

    // Check if this is a new occurrence of this speaker
    const isNewSpeakerOccurrence =
      index === 0 || segment.speaker !== segments[index - 1].speaker;

    // Only process if it's a new occurrence and we haven't hit the limit
    if (isNewSpeakerOccurrence && speakerOccurrences[speaker] < 3) {
      // Get frame 3 seconds after speaker starts
      // Since thumbnails are generated every second, we round to the nearest second
      const frameNumber = Math.floor(segment.start + 3);

      // Only add if the frame is within the segment duration
      if (frameNumber < segment.end) {
        speakerFrames.push({
          frameNumber,
          timestamp: segment.start + 3,
          speakerLabel: speaker,
          occurrenceNumber: speakerOccurrences[speaker] + 1,
        });

        speakerOccurrences[speaker]++;
      }
    }
  });

  return speakerFrames;
}

/**
 * Format timestamp for display (e.g., 1:23)
 */
export function formatTimestamp(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Get frame numbers for dense analysis (every N seconds for first occurrence)
 * Used for Pass 3 when other methods haven't identified the speaker
 */
export function getDenseFrameNumbersForSpeaker(
  segments: TranscriptSegment[],
  speakerLabel: string,
  intervalSeconds: number = 5,
  maxFrames: number = 10,
): FrameAnalysisRequest[] {
  const speakerFrames: FrameAnalysisRequest[] = [];

  // Find the first segment for this speaker
  const firstSegment = segments.find(seg => seg.speaker === speakerLabel);
  if (!firstSegment) return [];

  // Starting from 3 seconds after they start speaking
  let currentTime = firstSegment.start + 3;
  const endTime = Math.min(
    firstSegment.end,
    firstSegment.start + intervalSeconds * maxFrames,
  );

  let frameCount = 0;
  while (currentTime < endTime && frameCount < maxFrames) {
    speakerFrames.push({
      frameNumber: Math.floor(currentTime),
      timestamp: currentTime,
      speakerLabel: speakerLabel,
      occurrenceNumber: frameCount + 1,
    });

    currentTime += intervalSeconds;
    frameCount++;
  }

  return speakerFrames;
}

/**
 * The turn that stands for each speaker: their first stretch long enough to
 * hold the whole preview, or their longest if they never get one.
 *
 * Not simply their first stretch. Diarization routinely opens a speaker with a
 * fraction of a second — a single word misattributed inside someone else's
 * sentence, or a syllable over the intro music. On one real transcript two of
 * three speakers opened that way: 0.32s at 0:00 (the title card) and 0.24s at
 * 2:22 (inside the previous speaker's line). Both the still and the six seconds
 * the preview plays come off this turn, so a blip puts the wrong face in the
 * row *and* plays the wrong voice.
 *
 * "Long enough" is `PREVIEW_WINDOW_SECONDS` — the turn holds every second the
 * row plays, so the preview cannot wander into the next speaker. On a real
 * transcript two speakers of seven never held the floor that long anywhere in
 * forty minutes, which is why the fallback exists and why the caller still
 * clamps: their longest turn is the best on offer and it is shorter than the
 * window.
 *
 * `start` is where that turn begins, in seconds — what a preview plays from.
 * `end` is what stops a frame taken from it landing on the next speaker.
 *
 * Deliberately not `TranscriptSegment`s: building those needs an `end_ts` on
 * every element and yields nothing for transcripts that lack them, while a turn
 * boundary is just the point where `speaker_label` changes — which `ts` alone
 * gives. The last element's `end_ts` closes a turn where it exists, otherwise
 * its `ts` does, understating the turn slightly rather than running it past the
 * speaker.
 *
 * Removed and cut elements are skipped, so a turn is not split by an edit; a
 * `ts` is `null` at runtime for those anyway.
 *
 * Expects elements in chronological order, which is how transcripts are stored
 * — turns are cut where the label changes between *consecutive* elements, so
 * unordered input would run a turn from one appearance to another.
 *
 * (Unrelated to `services/transcript/speakerTurns.ts`, which groups the same
 * runs as *text* for a model to read and never looks at a timestamp.)
 */
export function getRepresentativeTurnBySpeaker(
  elements: Array<{
    type?: string;
    state?: string;
    speaker_label?: string;
    ts?: number | null;
    end_ts?: number | null;
  }>,
): Record<string, SpeakerTurnRange> {
  /** First turn per speaker long enough to play in full. */
  const settled: Record<string, SpeakerTurnRange> = {};
  const longest: Record<string, SpeakerTurnRange> = {};

  /**
   * A turn only ever grows, so this runs again on every element that extends
   * one: a turn that was too short a word ago may qualify now.
   */
  const consider = (turn: SpeakerTurnRange) => {
    if (
      !settled[turn.speaker] &&
      turn.start + PREVIEW_WINDOW_SECONDS <= turn.end
    ) {
      settled[turn.speaker] = turn;
    }
    const best = longest[turn.speaker];
    if (!best || turn.end - turn.start > best.end - best.start) {
      longest[turn.speaker] = turn;
    }
  };

  /** The run being walked. Held by reference, so it closes in place. */
  let current: SpeakerTurnRange | null = null;

  for (const element of elements) {
    if (element.type !== 'text') continue;
    if (element.state === 'removed' || element.state === 'cut') continue;
    if (!element.speaker_label || typeof element.ts !== 'number') continue;

    const end =
      typeof element.end_ts === 'number' ? element.end_ts : element.ts;

    if (current && current.speaker === element.speaker_label) {
      current.end = end;
    } else {
      current = { speaker: element.speaker_label, start: element.ts, end };
    }
    consider(current);
  }

  const representative: Record<string, SpeakerTurnRange> = {};
  for (const speaker of Object.keys(longest)) {
    representative[speaker] = settled[speaker] ?? longest[speaker];
  }

  return representative;
}

/**
 * Pick one representative video frame per speaker for use as a thumbnail.
 *
 * Steps 3 seconds into the speaker's representative turn so the shot has
 * settled, but no further than the turn itself. The turn is already chosen to
 * hold that frame (see `getRepresentativeTurnBySpeaker`); the clamp is for the
 * speaker who never holds the floor long enough anywhere, whose longest turn is
 * the best on offer.
 *
 * Frame files are one per second and 1-based (`000001.jpg`), so the result is
 * clamped to 1. No upper clamp against the video length is needed: a frame
 * inside a turn is a frame inside the video.
 */
export function getThumbnailFrameNumbersBySpeaker(
  representativeTurns: Record<string, SpeakerTurnRange>,
): Record<string, number> {
  const frameNumbers: Record<string, number> = {};

  for (const [speaker, { start, end }] of Object.entries(representativeTurns)) {
    const settled = Math.min(start + THUMBNAIL_SETTLE_SECONDS, end);
    frameNumbers[speaker] = Math.max(1, Math.floor(settled));
  }

  return frameNumbers;
}
